import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ExternalLink, Plus, Trash2, ChevronUp, ChevronDown, Upload, Star } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { InlineConfirm } from '@/components/ui/InlineConfirm';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Spinner, EmptyState } from '@/components/ui/EmptyState';
import { useToast } from '@/components/ui/ToastContext';
import { useProducts } from '@/contexts/ProductsContext';
import { supabase } from '@/lib/supabaseClient';
import { uploadPageImage, slugify } from '@/lib/customPageApi';
import type { LandingBlock, LandingPage } from '@/types/landingPage';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

function defaultBlock(type: LandingBlock['type']): LandingBlock {
  switch (type) {
    case 'hero':
      return { type: 'hero', heading: 'New headline', subheading: '', imageUrl: '' };
    case 'heading':
      return { type: 'heading', text: 'New heading', size: 'lg' };
    case 'paragraph':
      return { type: 'paragraph', text: '' };
    case 'image':
      return { type: 'image', url: '', alt: '' };
    case 'video':
      return { type: 'video', url: '' };
    case 'testimonials':
      return { type: 'testimonials', items: [] };
    case 'faq':
      return { type: 'faq', items: [] };
    case 'cta':
      return { type: 'cta', headline: 'Get the Set', buttonLabel: 'Buy Now' };
  }
}

// Image upload target — a single block field, or one item inside a
// testimonials block's items array (identified by its index).
type ImageTarget = { blockIndex: number; itemIndex?: number };

export default function AdminLandingPageDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { push } = useToast();
  const { routineSet, products } = useProducts();

  const [page, setPage] = useState<LandingPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');
  const [bundleSku, setBundleSku] = useState('');
  const [status, setStatus] = useState<'draft' | 'published'>('draft');
  const [seoDescription, setSeoDescription] = useState('');
  const [blocks, setBlocks] = useState<LandingBlock[]>([]);
  const [saving, setSaving] = useState(false);
  const [uploadingTarget, setUploadingTarget] = useState<ImageTarget | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingTargetRef = useRef<ImageTarget | null>(null);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    const { data } = await supabase
      .from('rxk_landing_pages')
      .select('id, slug, title, status, bundle_sku, blocks, seo_description')
      .eq('id', id)
      .maybeSingle();
    const p = data as LandingPage | null;
    setPage(p);
    if (p) {
      setTitle(p.title);
      setSlug(p.slug);
      setBundleSku(p.bundle_sku ?? '');
      setStatus(p.status);
      setSeoDescription(p.seo_description ?? '');
      setBlocks(p.blocks ?? []);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const updateBlock = (index: number, patch: Partial<LandingBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? ({ ...b, ...patch } as LandingBlock) : b)));
  };
  const removeBlock = (index: number) => setBlocks((prev) => prev.filter((_, i) => i !== index));
  const moveBlock = (index: number, dir: -1 | 1) => {
    setBlocks((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };
  const addBlock = (type: LandingBlock['type']) => setBlocks((prev) => [...prev, defaultBlock(type)]);

  const addTestimonial = (blockIndex: number) => {
    setBlocks((prev) =>
      prev.map((b, i) => (i === blockIndex && b.type === 'testimonials' ? { ...b, items: [...b.items, { photoUrl: '', name: '', rating: 5, quote: '' }] } : b)),
    );
  };
  const updateTestimonial = (blockIndex: number, itemIndex: number, patch: Partial<{ photoUrl: string; name: string; rating: number; quote: string }>) => {
    setBlocks((prev) =>
      prev.map((b, i) =>
        i === blockIndex && b.type === 'testimonials' ? { ...b, items: b.items.map((it, j) => (j === itemIndex ? { ...it, ...patch } : it)) } : b,
      ),
    );
  };
  const removeTestimonial = (blockIndex: number, itemIndex: number) => {
    setBlocks((prev) => prev.map((b, i) => (i === blockIndex && b.type === 'testimonials' ? { ...b, items: b.items.filter((_, j) => j !== itemIndex) } : b)));
  };

  const addFaqItem = (blockIndex: number) => {
    setBlocks((prev) => prev.map((b, i) => (i === blockIndex && b.type === 'faq' ? { ...b, items: [...b.items, { question: '', answer: '' }] } : b)));
  };
  const updateFaqItem = (blockIndex: number, itemIndex: number, patch: Partial<{ question: string; answer: string }>) => {
    setBlocks((prev) =>
      prev.map((b, i) => (i === blockIndex && b.type === 'faq' ? { ...b, items: b.items.map((it, j) => (j === itemIndex ? { ...it, ...patch } : it)) } : b)),
    );
  };
  const removeFaqItem = (blockIndex: number, itemIndex: number) => {
    setBlocks((prev) => prev.map((b, i) => (i === blockIndex && b.type === 'faq' ? { ...b, items: b.items.filter((_, j) => j !== itemIndex) } : b)));
  };

  const triggerImageUpload = (target: ImageTarget) => {
    pendingTargetRef.current = target;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = pendingTargetRef.current;
    e.target.value = '';
    if (!file || !target) return;
    setUploadingTarget(target);
    try {
      const url = await uploadPageImage(file);
      if (target.itemIndex !== undefined) {
        updateTestimonial(target.blockIndex, target.itemIndex, { photoUrl: url });
      } else {
        const block = blocks[target.blockIndex];
        if (block.type === 'hero') updateBlock(target.blockIndex, { imageUrl: url } as Partial<LandingBlock>);
        else if (block.type === 'image') updateBlock(target.blockIndex, { url } as Partial<LandingBlock>);
        else if (block.type === 'video') updateBlock(target.blockIndex, { posterUrl: url } as Partial<LandingBlock>);
      }
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to upload image');
    } finally {
      setUploadingTarget(null);
    }
  };

  const isUploading = (target: ImageTarget) =>
    !!uploadingTarget && uploadingTarget.blockIndex === target.blockIndex && uploadingTarget.itemIndex === target.itemIndex;

  const handleSave = async () => {
    if (!id) return;
    if (!slug.trim()) {
      push('error', 'Slug is required');
      return;
    }
    setSaving(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { error } = await supabase
        .from('rxk_landing_pages')
        .update({
          title,
          slug: slugify(slug),
          status,
          bundle_sku: bundleSku.trim() || null,
          blocks,
          seo_description: seoDescription || null,
          updated_by: user?.id ?? null,
        })
        .eq('id', id);
      if (error) throw error;
      push('success', status === 'published' ? 'Landing page saved and live' : 'Draft saved');
      await load();
    } catch (err) {
      push('error', err instanceof Error ? err.message : 'Failed to save landing page');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    const { error } = await supabase.from('rxk_landing_pages').delete().eq('id', id);
    if (error) {
      push('error', error.message);
      return;
    }
    push('success', 'Landing page deleted');
    navigate('/admin/landing-pages');
  };

  if (loading) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <Spinner />
      </AppShell>
    );
  }
  if (!page) {
    return (
      <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
        <EmptyState title="Landing page not found" />
      </AppShell>
    );
  }

  // Read-only preview of what the cta block will actually show — price/name
  // always come live from bundle_sku (the 3-step bundle, or a single
  // product's own slug), never typed into the block itself.
  const trimmedSku = bundleSku.trim();
  const matchedProduct = products.find((p) => p.id === trimmedSku);
  const ctaPreview = trimmedSku === '3-step-set' && routineSet ? routineSet : matchedProduct ? { name: matchedProduct.name, price: matchedProduct.price, originalPrice: undefined as string | undefined } : null;

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <div className="space-y-5">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/landing-pages')} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint cursor-pointer" aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-[20px] font-bold text-ink flex-1 truncate">{title || 'Untitled Landing Page'}</h1>
          <Badge tone={status === 'published' ? 'success' : 'neutral'}>{status}</Badge>
          {status === 'published' && (
            <a href={`/lp/${page.slug}`} target="_blank" rel="noopener noreferrer" className="text-ink-faint hover:text-ink">
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
          <InlineConfirm label="Delete" confirmLabel="Confirm delete" onConfirm={handleDelete} />
        </div>

        <Card>
          <CardHeader title="Page Settings" />
          <CardBody className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field label="Title (internal, not shown to visitors)">
                <Input value={title} onChange={(e) => setTitle(e.target.value)} />
              </Field>
              <Field label="URL slug (renders at /lp/slug)">
                <Input value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="3-step-set" />
              </Field>
            </div>
            <Field label="Bundle/product slug for the Buy button (optional)">
              <Input value={bundleSku} onChange={(e) => setBundleSku(e.target.value)} placeholder="3-step-set" />
            </Field>
            {ctaPreview && (
              <p className="text-[11px] text-success">
                Buy button will sell: <strong>{ctaPreview.name}</strong> — {ctaPreview.price}
                {ctaPreview.originalPrice && ` (was ${ctaPreview.originalPrice})`}
              </p>
            )}
            {bundleSku.trim() && !ctaPreview && (
              <p className="text-[11px] text-warning">No live product/bundle found for "{bundleSku.trim()}" — the Buy button won't work until this matches a real cart slug.</p>
            )}
            <Field label="SEO description (optional, page is always noindex)">
              <Textarea value={seoDescription} onChange={(e) => setSeoDescription(e.target.value)} rows={2} />
            </Field>
            <Field label="Status">
              <Select value={status} onChange={(e) => setStatus(e.target.value as 'draft' | 'published')} className="!w-48">
                <option value="draft">Draft (not visible)</option>
                <option value="published">Published (live)</option>
              </Select>
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Blocks"
            subtitle={`${blocks.length} block(s)`}
            action={
              <div className="flex flex-wrap items-center gap-1.5 justify-end">
                <Button size="sm" variant="secondary" onClick={() => addBlock('hero')}><Plus className="w-3 h-3" /> Hero</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('heading')}><Plus className="w-3 h-3" /> Heading</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('paragraph')}><Plus className="w-3 h-3" /> Paragraph</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('image')}><Plus className="w-3 h-3" /> Image</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('video')}><Plus className="w-3 h-3" /> Video</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('testimonials')}><Plus className="w-3 h-3" /> Testimonials</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('faq')}><Plus className="w-3 h-3" /> FAQ</Button>
                <Button size="sm" variant="secondary" onClick={() => addBlock('cta')}><Plus className="w-3 h-3" /> Buy CTA</Button>
              </div>
            }
          />
          <CardBody className="space-y-3">
            <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={handleFileChange} />
            {blocks.length === 0 ? (
              <EmptyState title="No blocks yet — add one above" />
            ) : (
              blocks.map((block, index) => (
                <div key={index} className="border border-border-subtle rounded-lg p-3 flex gap-3">
                  <div className="flex-1 space-y-2 min-w-0">
                    <Badge tone="neutral">{block.type}</Badge>

                    {block.type === 'hero' && (
                      <div className="space-y-2">
                        <Input value={block.heading} onChange={(e) => updateBlock(index, { heading: e.target.value })} placeholder="Headline" />
                        <Input value={block.subheading} onChange={(e) => updateBlock(index, { subheading: e.target.value })} placeholder="Subheading" />
                        <Input value={block.badge ?? ''} onChange={(e) => updateBlock(index, { badge: e.target.value })} placeholder="Badge text (optional, e.g. 'Bestseller')" />
                        {block.imageUrl && <img src={block.imageUrl} alt="" className="h-28 rounded-lg object-cover" />}
                        <Button size="sm" variant="secondary" onClick={() => triggerImageUpload({ blockIndex: index })} disabled={isUploading({ blockIndex: index })}>
                          <Upload className="w-3.5 h-3.5" /> {isUploading({ blockIndex: index }) ? 'Uploading…' : block.imageUrl ? 'Replace image' : 'Upload image'}
                        </Button>
                      </div>
                    )}

                    {block.type === 'heading' && (
                      <div className="flex gap-2">
                        <Input value={block.text} onChange={(e) => updateBlock(index, { text: e.target.value })} className="flex-1" />
                        <Select value={block.size} onChange={(e) => updateBlock(index, { size: e.target.value as 'lg' | 'xl' })} className="!w-24">
                          <option value="lg">Large</option>
                          <option value="xl">X-Large</option>
                        </Select>
                      </div>
                    )}

                    {block.type === 'paragraph' && <Textarea value={block.text} onChange={(e) => updateBlock(index, { text: e.target.value })} rows={3} />}

                    {block.type === 'image' && (
                      <div className="space-y-2">
                        {block.url && <img src={block.url} alt={block.alt} className="h-24 rounded-lg object-cover" />}
                        <Button size="sm" variant="secondary" onClick={() => triggerImageUpload({ blockIndex: index })} disabled={isUploading({ blockIndex: index })}>
                          <Upload className="w-3.5 h-3.5" /> {isUploading({ blockIndex: index }) ? 'Uploading…' : block.url ? 'Replace image' : 'Upload image'}
                        </Button>
                        <Input value={block.alt} onChange={(e) => updateBlock(index, { alt: e.target.value })} placeholder="Alt text" />
                        <Input value={block.caption ?? ''} onChange={(e) => updateBlock(index, { caption: e.target.value })} placeholder="Caption (optional)" />
                      </div>
                    )}

                    {block.type === 'video' && (
                      <div className="space-y-2">
                        <Input value={block.url} onChange={(e) => updateBlock(index, { url: e.target.value })} placeholder="Video URL (YouTube, Vimeo, or a direct .mp4 link)" />
                        <select
                          value={block.aspect ?? 'landscape'}
                          onChange={(e) => updateBlock(index, { aspect: e.target.value as 'landscape' | 'portrait' })}
                          className="w-full rounded-lg border border-border-subtle bg-surface px-3 py-2 text-[13px]"
                        >
                          <option value="landscape">Wide video (16:9)</option>
                          <option value="portrait">Vertical video (9:16, Reels)</option>
                        </select>
                        {block.posterUrl && <img src={block.posterUrl} alt="" className="h-24 rounded-lg object-cover" />}
                        <Button size="sm" variant="secondary" onClick={() => triggerImageUpload({ blockIndex: index })} disabled={isUploading({ blockIndex: index })}>
                          <Upload className="w-3.5 h-3.5" /> {isUploading({ blockIndex: index }) ? 'Uploading…' : block.posterUrl ? 'Replace poster' : 'Upload poster image (optional)'}
                        </Button>
                        <Input value={block.caption ?? ''} onChange={(e) => updateBlock(index, { caption: e.target.value })} placeholder="Caption (optional)" />
                      </div>
                    )}

                    {block.type === 'testimonials' && (
                      <div className="space-y-2.5">
                        {block.items.map((item, ti) => (
                          <div key={ti} className="rounded-lg border border-border-subtle p-2.5 space-y-2">
                            <div className="flex items-center gap-2">
                              {item.photoUrl && <img src={item.photoUrl} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />}
                              <Button size="sm" variant="secondary" onClick={() => triggerImageUpload({ blockIndex: index, itemIndex: ti })} disabled={isUploading({ blockIndex: index, itemIndex: ti })}>
                                <Upload className="w-3.5 h-3.5" /> {isUploading({ blockIndex: index, itemIndex: ti }) ? 'Uploading…' : item.photoUrl ? 'Replace photo' : 'Upload photo'}
                              </Button>
                              <button onClick={() => removeTestimonial(index, ti)} className="ml-auto w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer flex-shrink-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <Input value={item.name} onChange={(e) => updateTestimonial(index, ti, { name: e.target.value })} placeholder="Customer name" />
                              <div className="flex items-center gap-1">
                                {[1, 2, 3, 4, 5].map((n) => (
                                  <button key={n} type="button" onClick={() => updateTestimonial(index, ti, { rating: n })} className="cursor-pointer">
                                    <Star className={`w-4 h-4 ${n <= item.rating ? 'fill-warning text-warning' : 'text-border'}`} />
                                  </button>
                                ))}
                              </div>
                            </div>
                            <Textarea value={item.quote} onChange={(e) => updateTestimonial(index, ti, { quote: e.target.value })} rows={2} placeholder="What did they say?" />
                          </div>
                        ))}
                        <Button size="sm" variant="secondary" onClick={() => addTestimonial(index)}><Plus className="w-3.5 h-3.5" /> Add Testimonial</Button>
                      </div>
                    )}

                    {block.type === 'faq' && (
                      <div className="space-y-2.5">
                        {block.items.map((item, fi) => (
                          <div key={fi} className="rounded-lg border border-border-subtle p-2.5 space-y-2">
                            <div className="flex items-center gap-2">
                              <Input value={item.question} onChange={(e) => updateFaqItem(index, fi, { question: e.target.value })} placeholder="Question" className="flex-1" />
                              <button onClick={() => removeFaqItem(index, fi)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer flex-shrink-0">
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                            <Textarea value={item.answer} onChange={(e) => updateFaqItem(index, fi, { answer: e.target.value })} rows={2} placeholder="Answer" />
                          </div>
                        ))}
                        <Button size="sm" variant="secondary" onClick={() => addFaqItem(index)}><Plus className="w-3.5 h-3.5" /> Add Question</Button>
                      </div>
                    )}

                    {block.type === 'cta' && (
                      <div className="space-y-2">
                        <Input value={block.headline} onChange={(e) => updateBlock(index, { headline: e.target.value })} placeholder="Headline (e.g. 'Get the Complete Set')" />
                        <Input value={block.buttonLabel} onChange={(e) => updateBlock(index, { buttonLabel: e.target.value })} placeholder="Button label (e.g. 'Buy Now')" />
                        <p className="text-[10.5px] text-ink-faint">Price, product name, and image come live from the "Bundle/product slug" field above — set that, not here.</p>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <button onClick={() => moveBlock(index, -1)} disabled={index === 0} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => moveBlock(index, 1)} disabled={index === blocks.length - 1} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-surface-muted text-ink-faint disabled:opacity-30 cursor-pointer">
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => removeBlock(index)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-error-bg text-error cursor-pointer">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </CardBody>
        </Card>

        <Button onClick={handleSave} loading={saving}>Save</Button>
      </div>
    </AppShell>
  );
}
